import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  Stack,
  Group,
  Text,
  Badge,
  Code,
  Table,
  Paper,
  Button,
  CopyButton,
  Alert,
  ScrollArea,
  Tabs,
  Divider,
} from '@mantine/core';
import {
  IconCopy,
  IconCheck,
  IconDownload,
  IconInfoCircle,
  IconBook,
  IconPackage,
  IconCode,
  IconSparkles,
  IconWorld,
  IconChartBar,
  IconRocket,
  IconUser,
} from '@tabler/icons-react';

interface CustomUiDevGuideModalProps {
  opened: boolean;
  onClose: () => void;
}

export const SAMPLE_CAKECHAT_JSON = `{
  "name": "CakeChat Custom Theme",
  "version": "1.0.0",
  "description": "自訂聊天室前端介面主題",
  "author": "Your Team",
  "mode": "window",
  "entryFile": "index.html",
  "botConfig": {
    "theme": {
      "themeColor": "#3b82f6"
    },
    "layout": {
      "windowWidth": 420,
      "windowHeight": 680
    },
    "text": {
      "title": "AI 智能助理"
    }
  }
}`;

export const SAMPLE_INDEX_HTML = `<!DOCTYPE html>
<html lang="zh-TW">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>CakeChat Custom UI</title>
  <!-- 引入平台託管的 Headless SDK -->
  <script src="/api/WebBot/sdk"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
    body { background: #0f172a; color: #f8fafc; height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
    
    /* 頂部標題列 */
    .header { padding: 10px 14px; background: #1e293b; border-bottom: 1px solid #334155; display: flex; align-items: center; justify-content: space-between; }
    .header-left { display: flex; align-items: center; gap: 8px; }
    .avatar { width: 30px; height: 30px; border-radius: 50%; object-fit: cover; background: #334155; display: none; }
    .title-box { display: flex; flex-direction: column; }
    .title { font-size: 13px; font-weight: 600; }
    .user-tag { font-size: 10px; color: #94a3b8; font-family: monospace; }
    .header-right { display: flex; align-items: center; gap: 6px; }
    .status-dot { width: 7px; height: 7px; border-radius: 50%; background: #94a3b8; display: inline-block; }
    .status-dot.connected { background: #22c55e; }
    .status-dot.connecting { background: #eab308; }
    .status-dot.disconnected { background: #ef4444; }
    .icon-btn { background: transparent; border: none; color: #94a3b8; font-size: 14px; cursor: pointer; padding: 4px 6px; border-radius: 4px; }
    .icon-btn:hover { background: #334155; color: #fff; }

    /* 聊天歷史訊息清單 */
    .messages-area { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
    .message-row { display: flex; flex-direction: column; max-width: 84%; }
    .message-row.user { align-self: flex-end; align-items: flex-end; }
    .message-row.assistant { align-self: flex-start; align-items: flex-start; }
    .bubble { padding: 9px 13px; border-radius: 12px; font-size: 13px; line-height: 1.5; word-break: break-word; white-space: pre-wrap; }
    .message-row.user .bubble { background: #3b82f6; color: #ffffff; border-bottom-right-radius: 2px; }
    .message-row.assistant .bubble { background: #1e293b; color: #f1f5f9; border: 1px solid #334155; border-bottom-left-radius: 2px; }
    .media-img { max-width: 100%; border-radius: 8px; margin-top: 6px; display: block; }
    
    /* 評分 */
    .feedback-row { display: flex; gap: 4px; margin-top: 3px; }
    .feedback-btn { background: #334155; border: none; color: #cbd5e1; font-size: 11px; padding: 2px 6px; border-radius: 4px; cursor: pointer; }
    .feedback-btn.active { background: #2563eb; color: #fff; }

    /* 輸入區塊 */
    .input-area { padding: 10px 14px; background: #1e293b; border-top: 1px solid #334155; display: flex; gap: 8px; }
    .input-box { flex: 1; background: #0f172a; border: 1px solid #334155; border-radius: 8px; padding: 8px 12px; color: #fff; font-size: 13px; outline: none; }
    .input-box:focus { border-color: #3b82f6; }
    .send-btn { background: #3b82f6; color: white; border: none; padding: 0 14px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; }
    .send-btn:disabled { background: #475569; cursor: not-allowed; }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <img id="botAvatar" class="avatar" alt="Avatar" />
      <div class="title-box">
        <span id="titleText" class="title">AI 智能助理</span>
        <span id="userIdBadge" class="user-tag" title="訪客 ID">連線中...</span>
      </div>
    </div>
    <div class="header-right">
      <span id="statusDot" class="status-dot" title="連線狀態"></span>
      <button id="clearBtn" class="icon-btn" title="清空對話紀錄">🗑️</button>
      <button id="closeBtn" class="icon-btn" title="收合聊天室">&times;</button>
    </div>
  </div>

  <div id="messagesArea" class="messages-area"></div>

  <div class="input-area">
    <input id="chatInput" class="input-box" placeholder="輸入訊息..." />
    <button id="sendBtn" class="send-btn">發送</button>
  </div>

  <script>
    // 零設定初始化：自動從網址參數解析 botProviderId, host, userId, userToken 等
    // 若整合外部會員系統，可傳入: new CakeChat.CakeChatClient({ userId: 'member_123' })
    const client = new CakeChat.CakeChatClient();

    const messagesArea = document.getElementById('messagesArea');
    const chatInput = document.getElementById('chatInput');
    const sendBtn = document.getElementById('sendBtn');
    const statusDot = document.getElementById('statusDot');
    const titleText = document.getElementById('titleText');
    const botAvatar = document.getElementById('botAvatar');
    const userIdBadge = document.getElementById('userIdBadge');
    const closeBtn = document.getElementById('closeBtn');
    const clearBtn = document.getElementById('clearBtn');

    // 顯示訪客識別碼 (User ID)
    userIdBadge.textContent = '訪客: ' + client.getUserId();

    // 視窗關閉與清空對話
    closeBtn.onclick = () => CakeChat.closeWidget();
    clearBtn.onclick = () => {
      if (confirm('確定清空聊天紀錄？')) client.clearHistory();
    };

    // 取得後台配置與頭像
    client.fetchConfig().then(cfg => {
      if (cfg?.text?.title) titleText.textContent = cfg.text.title;
      const avatarUrl = client.getAvatarUrl();
      if (avatarUrl) {
        botAvatar.src = avatarUrl;
        botAvatar.style.display = 'block';
      }
    }).catch(console.warn);

    // 訂閱狀態更新並渲染 UI
    client.subscribe((state) => {
      statusDot.className = 'status-dot ' + state.connectionStatus;
      sendBtn.disabled = state.isProcessing || !chatInput.value.trim();

      messagesArea.innerHTML = '';
      state.messages.forEach(msg => {
        const row = document.createElement('div');
        row.className = 'message-row ' + msg.role;
        
        const bubble = document.createElement('div');
        bubble.className = 'bubble';
        bubble.textContent = msg.message;

        // 渲染多媒體圖片 (若有)
        if (msg.mediaUrls && msg.mediaUrls.length > 0) {
          msg.mediaUrls.forEach(m => {
            const img = document.createElement('img');
            img.src = m.url;
            img.className = 'media-img';
            bubble.appendChild(img);
          });
        }
        row.appendChild(bubble);

        // AI 回覆評分按鈕
        if (msg.role === 'assistant' && msg.historyId) {
          const fbRow = document.createElement('div');
          fbRow.className = 'feedback-row';
          const goodBtn = document.createElement('button');
          goodBtn.className = 'feedback-btn ' + (msg.feedback?.rating === 'Good' ? 'active' : '');
          goodBtn.textContent = '👍';
          goodBtn.onclick = () => client.feedbackMessage(msg.historyId, 'Good');

          const badBtn = document.createElement('button');
          badBtn.className = 'feedback-btn ' + (msg.feedback?.rating === 'Bad' ? 'active' : '');
          badBtn.textContent = '👎';
          badBtn.onclick = () => client.feedbackMessage(msg.historyId, 'Bad');

          fbRow.appendChild(goodBtn);
          fbRow.appendChild(badBtn);
          row.appendChild(fbRow);
        }

        messagesArea.appendChild(row);
      });
      messagesArea.scrollTop = messagesArea.scrollHeight;
    });

    // 輸入監聽與發送
    chatInput.oninput = () => {
      client.setInput(chatInput.value);
      sendBtn.disabled = !chatInput.value.trim() || client.getState().isProcessing;
    };

    const handleSend = () => {
      if (chatInput.value.trim() && !client.getState().isProcessing) {
        client.sendMessage();
        chatInput.value = '';
      }
    };

    chatInput.onkeydown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };
    sendBtn.onclick = handleSend;

    // 啟動連線
    client.connect();
  </script>
</body>
</html>`;

export const SAMPLE_REACT_EXAMPLE = `import React from 'react';

// 若專案使用 React，SDK 提供工廠函式直接生成 Hooks:
const { CakeChatProvider, useCakeChat } = window.CakeChat.createReactHooks(React);

export function CustomChatApp() {
  const {
    client,
    messages,
    input,
    setInput,
    sendMessage,
    isProcessing,
    isStreaming,
    connectionStatus,
    feedbackMessage,
    clearHistory
  } = useCakeChat();

  const userId = client?.getUserId();

  return (
    <div className="chat-container">
      <header className="header">
        <div>
          <span>連線狀態: {connectionStatus}</span>
          <small style={{ marginLeft: 8, opacity: 0.7 }}>訪客: {userId}</small>
        </div>
        <div>
          <button onClick={() => clearHistory()}>清空</button>
          <button onClick={() => window.CakeChat.closeWidget()}>收合</button>
        </div>
      </header>

      <div className="message-list">
        {messages.map((msg, idx) => (
          <div key={idx} className={\`msg \${msg.role}\`}>
            <p>{msg.message}</p>
            {msg.mediaUrls?.map((m, mIdx) => (
              <img key={mIdx} src={m.url} alt="media" className="msg-media" />
            ))}
            {msg.role === 'assistant' && msg.historyId && (
              <div className="feedback">
                <button onClick={() => feedbackMessage(msg.historyId, 'Good')}>👍</button>
                <button onClick={() => feedbackMessage(msg.historyId, 'Bad')}>👎</button>
              </div>
            )}
          </div>
        ))}
        {isStreaming && <div className="typing">AI 正在輸入...</div>}
      </div>

      <div className="input-bar">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="輸入訊息..."
        />
        <button disabled={isProcessing || !input.trim()} onClick={() => sendMessage()}>
          送出
        </button>
      </div>
    </div>
  );
}`;

// Technical SDK API Markdown Specification (Full Developer Specification in English)
export function buildCustomUiSdkSpecMarkdown(sdkDts?: string) {
  const dtsSnippet = sdkDts
    ? sdkDts.trim()
    : `// From CakeChat Headless SDK (/api/WebBot/sdk.d.ts)
// Access or download /api/WebBot/sdk.d.ts for the latest BotConfig and SDK type declarations`;

  return `# CakeChat Custom UI Specification & Headless SDK Technical Manual

This document is the official developer reference manual for CakeChat Custom UI and Headless SDK. It details the runtime architecture, visitor identity (User ID) and authentication integration, project manifest specifications, global window controls, client APIs, state models, and event protocols.

---

## 1. Architecture & Sandboxed Iframe Runtime

CakeChat supports uploading custom frontend interfaces (Custom UI) to completely replace the default chat widget with fully customized web pages, styles, and interactions.

- **Sandboxed Iframe Execution**: Custom UI is mounted inside a sandboxed \`<iframe>\` managed by the host \`<cakechat-widget>\` container. This isolates DOM, CSS styles, and script scopes, completely preventing style bleeding or script collisions with the host website.
- **Static Asset Hosting**: Upon uploading a packaged \`.zip\` archive, static assets are hosted on the server and served under \`/api/WebBot/custom/{botProviderId}/{filePath}\`.
- **Relative Path Requirement**: All internal resource references in HTML/CSS/JS (e.g., stylesheets, scripts, images, web fonts) **must use relative paths** (e.g. \`./style.css\` or \`assets/app.js\`). Do not use root-relative absolute paths (e.g. \`/style.css\`).
- **Platform-Managed SDK**: Include the platform-hosted Headless SDK script in the \`<head>\` of your entry HTML:
  \`\`\`html
  <script src="/api/WebBot/sdk"></script>
  \`\`\`
  Once loaded, the global \`window.CakeChat\` object and its alias \`window.CakeChatSDK\` become available. TypeScript declarations are hosted at \`/api/WebBot/sdk.d.ts\`.
- **Clean Deployment**: Uploading a new ZIP package clears previous Custom UI files for that bot before extracting new assets, ensuring zero orphaned files.
- **Automatic Common Prefix Stripping**: If all files in the ZIP archive reside under a single top-level directory (e.g. \`dist/\` or \`my-theme/\`), the extraction engine automatically strips that wrapper directory, placing all contents directly at the root.

---

## 2. Visitor Identity, Session Management & Member Authentication (User ID & Auth)

### 2.1 Role of \`userId\`
In CakeChat, \`userId\` is the **primary partition key** for user conversations:
1. **Conversation History Isolation**: Chat history endpoints (\`/api/WebBot/history/{botProviderId}/{userId}\`) and history clearing endpoints (\`/api/WebBot/clear-history/{botProviderId}/{userId}\`) are strictly scoped by \`(botProviderId, userId)\`.
2. **Real-time SignalR Channel**: When establishing a real-time connection, the client invokes \`ListenEvent(botProviderId, userId, userToken)\` to join the dedicated SignalR push group.
3. **Satisfaction Ratings & Issue Reporting**: Feedbacks and reports submitted via \`feedbackMessage\` and \`reportIssue\` are associated with the active \`userId\`.
4. **Multi-Tab Session Sync**: Browser tabs sharing the same \`userId\` automatically access and share the same historical messages.

### 2.2 4-Tier Resolution Priority
When \`CakeChatClient\` is initialized without explicit options, \`userId\` is resolved using a 4-tier priority cascade:
1. **Explicit Option**: Passed via \`new CakeChat.CakeChatClient({ userId: '...' })\`.
2. **URL Query Parameter**: Extracted from \`?userId=...\` in \`window.location.search\` (automatically forwarded by the host container).
3. **Storage Cache**: Checked against the configured storage adapter using key \`\${storageKeyPrefix}\${botProviderId}\` (default prefix: \`chatbot_userid_\`).
4. **Auto-Generated UUID**: If none of the above exists, the SDK generates a persistent anonymous identifier \`user_\${crypto.randomUUID()}\` and saves it to storage.

### 2.3 External Website Member System Integration
When integrating CakeChat into a website with authenticated members (e.g. \`member_1001\`), link the logged-in user identity using either of the following approaches:

#### Approach A: Via Host Web Component (\`<cakechat-widget>\`)
Pass \`user-id\` and optional \`user-token\` directly to the host custom element on your site:
\`\`\`html
<cakechat-widget
  bot-provider-id="65d4fa3901b2a94f1c998a12"
  user-id="member_1001"
  user-token="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
></cakechat-widget>
\`\`\`
The host container will automatically append \`userId=member_1001\` and \`userToken=...\` to the Custom UI iframe query parameters, enabling zero-config pickup by \`new CakeChat.CakeChatClient()\`.

#### Approach B: Via Headless SDK Constructor (Direct Initialization)
If developing a custom standalone UI or embedding directly:
\`\`\`javascript
const client = new CakeChat.CakeChatClient({
  userId: 'member_1001',
  userToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
});
\`\`\`

### 2.4 Visitor Authentication Token (\`userToken\`)
If the bot administrator has enabled visitor identity verification in the backend:
- The host must supply a valid signed JWT via \`userToken\`.
- The SDK sends \`userToken\` during SignalR handshake (\`ListenEvent\`) and HTTP request headers.
- If verification fails or expires, the connection transitions to \`disconnected\` with an authentication error message.

### 2.5 Storage Adapters & Persistence Lifecycle
The SDK provides flexible storage strategies via the \`storage\` option in \`CakeChatClientOptions\`:
| Storage Mode | Persistence Scope | Description |
| :--- | :--- | :--- |
| \`'local'\` (**Default**) | Cross-tab & persistent across browser restarts | Saves anonymous \`userId\` to \`localStorage\`. Returning visitors retain their historical conversations. |
| \`'session'\` | Current browser tab only | Saves to \`sessionStorage\`. Closing the tab or opening a new tab generates an isolated conversation. |
| \`'memory'\` | Volatile in-memory | Stores state in a Map. Refreshing the page completely resets visitor identity. |
| \`StorageAdapter\` | Custom | Pass an object implementing \`{ getItem, setItem, removeItem }\` to integrate with custom storage. |

### 2.6 Reading Active User ID
Use \`client.getUserId()\` at runtime to inspect the active visitor identifier:
\`\`\`javascript
const activeUserId = client.getUserId();
console.log('Current visitor identifier:', activeUserId);
\`\`\`

---

## 3. Project Structure & cakechat.json Specification

### Recommended Archive Root Structure
\`\`\`text
my-custom-ui.zip
├── cakechat.json       (UI specification & recommended botConfig manifest)
├── index.html          (Entry HTML page)
├── style.css           (Stylesheets referenced via relative paths)
├── app.js              (Frontend application logic)
└── assets/             (Static assets: images, fonts, icons)
    └── logo.png
\`\`\`

### cakechat.json Field Reference
\`\`\`json
{
  "name": "CakeChat Modern Dark",
  "version": "1.0.0",
  "description": "Modern dark rounded bubble theme",
  "author": "CakeChat Team",
  "mode": "window",
  "entryFile": "index.html",
  "botConfig": {
    "theme": {
      "themeColor": "#3b82f6"
    },
    "layout": {
      "windowWidth": 420,
      "windowHeight": 680
    },
    "text": {
      "title": "AI Assistant"
    }
  }
}
\`\`\`

| Field Name | Type | Default | Description |
| :--- | :--- | :--- | :--- |
| \`name\` | \`string\` | \`null\` | Theme name, e.g. \`"Cyberpunk Dark Theme"\` |
| \`version\` | \`string\` | \`"1.0.0"\` | Semantic version string |
| \`description\` | \`string\` | \`null\` | UI theme summary or feature highlights |
| \`author\` | \`string\` | \`null\` | Developer or organization name |
| \`mode\` | \`string\` | \`"window"\` | Operating mode: \`"window"\` (floating modal with resize/collapse) or \`"headless"\` (unconstrained container-filling / embed mode, alias \`"full"\`) |
| \`entryFile\` | \`string\` | \`"index.html"\` | Relative path to entry HTML document |
| \`botConfig\` | \`object\` | \`null\` | Recommended bot appearance and config overrides (maps to SDK \`BotConfig\`). When uploaded, the admin console offers a one-click apply prompt. |

### SDK BotConfig Type Reference
\`\`\`typescript
${dtsSnippet}
\`\`\`

---

## 4. URL Query Parameter Auto-Injection

When the host container mounts the Custom UI iframe, it appends the following parameters to the URL:

| Parameter | Type | Description | Example |
| :--- | :--- | :--- | :--- |
| \`botProviderId\` | \`string\` | Target Bot Provider ID (Required for connection) | \`"65d4fa3901b2a94f1c998a12"\` |
| \`host\` | \`string\` | Server backend API & SignalR host URL | \`"https://api.example.com"\` |
| \`userId\` | \`string\` | Visitor/member identifier (automatically forwarded) | \`"member_1001"\` |
| \`userToken\` | \`string\` | Visitor JWT authentication token (when visitor auth is enabled) | \`"eyJhbGciOi..."\` |
| \`previewMode\` | \`string\` | Set to \`"true"\` during admin live preview sessions | \`"true"\` |
| \`mode\` | \`string\` | Active display mode (\`"window"\` or \`"headless"\`) | \`"window"\` |

---

## 5. Global Window Controls & Utilities (\`window.CakeChat\`)

The global \`window.CakeChat\` object provides container communication utilities and asset URL resolvers:

| Method / Property | Signature | Return Type | Description |
| :--- | :--- | :--- | :--- |
| \`CakeChat.closeWidget()\` | \`() => void\` | \`void\` | Notify host container to collapse/close the floating chat window |
| \`CakeChat.openWidget()\` | \`() => void\` | \`void\` | Notify host container to expand/open the floating chat window |
| \`CakeChat.toggleWidget()\` | \`() => void\` | \`void\` | Toggle floating chat window between expanded and collapsed |
| \`CakeChat.getSearchParams()\` | \`() => Record<string, string>\` | \`Record<string, string>\` | Parse and return all URL query parameters as a key-value object |
| \`CakeChat.fetchBotConfig(options)\` | \`(options: { host?: string, botProviderId: string }) => Promise<BotConfig>\` | \`Promise<BotConfig>\` | Fetch remote bot appearance and text configuration from API |
| \`CakeChat.getBotAvatarUrl(options?)\` | \`(options?: AssetUrlOptions) => string \\| null\` | \`string \\| null\` | Resolve full URL for the bot avatar image |
| \`CakeChat.getBotWidgetIconUrl(options?)\` | \`(options?: AssetUrlOptions) => string \\| null\` | \`string \\| null\` | Resolve full URL for the launcher widget icon |
| \`CakeChat.getBotBackgroundImageUrl(options?)\` | \`(options?: AssetUrlOptions) => string \\| null\` | \`string \\| null\` | Resolve full URL for the chat background image |
| \`CakeChat.createReactHooks(React)\` | \`(React: any) => ReactHooksBundle\` | \`{ CakeChatProvider, useCakeChat, useBotConfig, CakeChatExtension }\` | Generate React context provider, custom hooks, and extension component bundle |

---

## 5.1 Declarative Extension Mascot Tag (\`<cakechat-extension>\`) & React Component (\`<CakeChatExtension />\`)

CakeChat SDK provides a zero-config, declarative Web Component \`<cakechat-extension>\` and React component \`<CakeChatExtension />\` for embedding mascot extensions, Spine 2D characters, and interactive widgets anywhere in your custom UI:

### HTML / Vue / Vanilla Usage:
\`\`\`html
<!-- Default: loads the 1st enabled extension from bot config -->
<cakechat-extension></cakechat-extension>

<!-- Target specific bundle or slot index -->
<cakechat-extension bundle="my-spine-character"></cakechat-extension>
<cakechat-extension index="1"></cakechat-extension>

<!-- Locked manual state -->
<cakechat-extension state="speaking" state-locked></cakechat-extension>
\`\`\`

### React Usage:
\`\`\`tsx
import React from 'react';

// 透過 createReactHooks 綁定專案的 React 實例
const { CakeChatProvider, CakeChatExtension, useCakeChat } = window.CakeChat.createReactHooks(React);

function CustomChatView() {
  return (
    <div className="custom-layout">
      <CakeChatExtension bundle="my-mascot" onClick={(e) => console.log('Clicked', e.detail)} />
      <div className="chat-area">...</div>
    </div>
  );
}
\`\`\`

### Attributes & Props:
| Attribute / Prop | Type | Default | Description |
| :--- | :--- | :--- | :--- |
| \`index\` | \`number\` | \`0\` | Zero-based slot index in bot configuration |
| \`bundle\` | \`string\` | \`undefined\` | Specific Asset Bundle path |
| \`slot-id\` / \`slotId\` | \`string\` | \`undefined\` | Extension slot ID in bot configuration |
| \`width\` | \`string \| number\` | Auto | Extension container width (e.g. \`280\`, \`100%\`) |
| \`height\` | \`string \| number\` | Auto | Extension container height (e.g. \`360\`, \`auto\`) |
| \`state\` | \`string\` | Auto from client | Current animation state (\`idle\`, \`thinking\`, \`speaking\`, \`sleep\`, \`error\`) |
| \`state-locked\` / \`stateLocked\` | \`boolean\` | \`false\` | When true, pauses automatic synchronization with chat client events |
| \`bot-provider-id\` / \`botProviderId\` | \`string\` | Auto | Target Bot Provider ID |
| \`host\` | \`string\` | Auto | API host origin |

### DOM Methods on \`<cakechat-extension>\`:
| Method | Signature | Description |
| :--- | :--- | :--- |
| \`setState(state, lock?)\` | \`(state: string, lock?: boolean) => void\` | Set state and optionally lock against auto-sync |
| \`playAnimation(name, options?)\` | \`(name: string, options?: { loop?: boolean }) => void\` | Play specific animation track |
| \`resetState()\` | \`() => void\` | Unlock and resume automatic synchronization with client |
| \`reload()\` | \`() => Promise<void>\` | Refresh manifest and re-render extension |

---

## 6. CakeChatClient API Reference

### 6.1 Constructor Options (\`CakeChatClientOptions\`)
\`\`\`typescript
interface CakeChatClientOptions {
  botProviderId?: string; // Auto-parsed from URL by default
  host?: string;          // Auto-parsed from URL or window.location.origin
  userId?: string;        // Explicit user ID; falls back to URL param or storage
  userToken?: string;     // Visitor JWT auth token
  storage?: 'local' | 'session' | 'memory' | StorageAdapter; // Default 'local'
  storageKeyPrefix?: string; // Default 'chatbot_userid_'
  autoConnect?: boolean;  // Default false
  sleepTimeout?: number;  // Idle timeout in seconds, default 60 (0 disables)
  dispatchDomEvents?: boolean; // Dispatch custom DOM events on window, default false
  domEventTarget?: EventTarget | null;
  previewMode?: boolean;  // Live mock preview mode without backend persistence
  feedbackNotificationText?: string | null;
  initialConfig?: Partial<BotConfig> | string | null;
}
\`\`\`

### 6.2 Complete Client Methods
| Method Name | Parameters | Return Type | Category | Description |
| :--- | :--- | :--- | :--- | :--- |
| \`connect()\` | None | \`Promise<void>\` | Connection | Connect to SignalR hub and automatically load chat history |
| \`disconnect()\` | None | \`Promise<void>\` | Connection | Disconnect active real-time connection |
| \`retryConnection()\` | None | \`Promise<void>\` | Connection | Manually reconnect SignalR transport |
| \`loadHistory(silent?)\` | \`silent?: boolean\` | \`Promise<void>\` | History | Reload chat history from server |
| \`clearHistory()\` | None | \`Promise<void>\` | History | Clear both local store and remote server chat history for current user |
| \`sendMessage(text?, retryId?)\` | \`overrideText?: string, retryHistoryId?: string\` | \`Promise<void>\` | Messaging | Send a user message (uses current input if text is omitted) |
| \`retryMessage(historyId)\` | \`historyId: string\` | \`Promise<void>\` | Messaging | Resend a previously failed message |
| \`setInput(text)\` | \`text: string\` | \`void\` | Messaging | Update input text snapshot and reset idle sleep timer |
| \`wake()\` | None | \`void\` | State | Wake sleeping bot (transitions to \`'idle'\`) and reset idle timer |
| \`feedbackMessage(id, rating, comment?)\` | \`id: string, rating: 'Good' \\| 'Bad', comment?: string\` | \`Promise<void>\` | Feedback | Submit message rating and optional comment |
| \`reportIssue(id, comment)\` | \`id: string, comment: string\` | \`Promise<void>\` | Feedback | Submit an issue report for a specific assistant message |
| \`setFeedbackNotificationText(text)\` | \`text?: string \\| null\` | \`void\` | Feedback | Dynamically update toast notification text after rating |
| \`getState()\` | None | \`CakeChatState\` | State | Get snapshot of current reactive state |
| \`getUserId()\` | None | \`string\` | Identity | Get active resolved visitor / member identifier |
| \`getBotState()\` | None | \`BotState\` | State | Get current bot state (\`'idle'\` \\| \`'sleep'\` \\| \`'thinking'\` \\| \`'streaming'\` \\| \`'error'\`) |
| \`getConfig()\` | None | \`BotConfig \\| null\` | Config | Get cached remote bot configuration |
| \`isConfigLoaded()\` | None | \`boolean\` | Config | Check if remote bot configuration has loaded |
| \`fetchConfig(forceReload?)\` | \`forceReload?: boolean\` | \`Promise<BotConfig>\` | Config | Fetch or refresh bot configuration from API |
| \`getAvatarUrl(options?)\` | \`{ fallback?: string }\` | \`string \\| null\` | Assets | Get bot avatar image URL |
| \`getWidgetIconUrl(options?)\` | \`{ fallback?: string }\` | \`string \\| null\` | Assets | Get bot launcher widget icon URL |
| \`getBackgroundImageUrl(options?)\` | \`{ fallback?: string }\` | \`string \\| null\` | Assets | Get chat window background image URL |
| \`subscribe(listener)\` | \`(state, prevState) => void\` | \`() => void\` | Events | Subscribe to state changes; returns unsubscribe function |
| \`on(event, handler)\` | \`event: string, handler: Function\` | \`() => void\` | Events | Register event listener; returns unbind function |
| \`off(event, handler)\` | \`event: string, handler: Function\` | \`void\` | Events | Remove registered event listener |
| \`destroy()\` | None | \`void\` | Lifecycle | Teardown timers, transport, and remove event listeners |

---

## 7. CakeChatState Snapshot & Data Models

### CakeChatState Fields
| Field Name | Type | Description |
| :--- | :--- | :--- |
| \`messages\` | \`ChatRecord[]\` | List of chat records (both user and assistant) |
| \`input\` | \`string\` | Current input field text snapshot |
| \`isProcessing\` | \`boolean\` | Whether AI is generating a reply (disable send button) |
| \`isStreaming\` | \`boolean\` | Whether AI is actively streaming token chunks |
| \`isLoadingHistory\` | \`boolean\` | Whether chat history is loading from the server |
| \`botState\` | \`'idle' \\| 'sleep' \\| 'thinking' \\| 'streaming' \\| 'error'\` | Bot state machine status |
| \`connectionStatus\` | \`'disconnected' \\| 'connecting' \\| 'connected' \\| 'reconnecting'\` | SignalR connection status |
| \`error\` | \`string \\| null\` | Active error message or null |
| \`notification\` | \`string \\| null\` | Push notification toast message or null |

### ChatRecord Data Structure
\`\`\`typescript
interface ChatRecord {
  role: 'user' | 'assistant' | string;
  message: string;             // Markdown formatted text
  historyId?: string;          // Unique record ID (used for ratings & retry)
  status?: 'sending' | 'sent' | 'failed'; // User message status
  feedback?: {
    rating?: 'Good' | 'Bad';
    comment?: string;
    timestamp?: number;
  };
  timestamp?: number;
  mediaUrls?: Array<{ url: string; type: string }>; // Images / attachments
}
\`\`\`

---

## 8. Event Listener Reference & Iframe postMessage Protocol

### Event Bus (\`client.on\`)
| Event Name | Payload Data | Trigger Condition |
| :--- | :--- | :--- |
| \`stateChange\` | \`{ state: BotState, previousState: BotState, isThinking: boolean, isStreaming: boolean }\` | Bot state machine transition (e.g. sleep or thinking) |
| \`messageReceived\` | \`{ role: string, message: string, text: string, historyId?: string, isComplete: boolean }\` | Incoming AI reply text or streaming chunk |
| \`messageSent\` | \`{ text: string, message: string, timestamp: number, historyId?: string }\` | Outgoing user message successfully dispatched |
| \`connectionStatusChange\` | \`{ status: ConnectionStatus, previousStatus: ConnectionStatus, error?: string }\` | Real-time connection status change |
| \`error\` | \`{ error: string }\` | Network or runtime error occurred |
| \`notification\` | \`{ message: string }\` | System push notification received |
| \`configLoaded\` | \`{ config: BotConfig }\` | Remote bot configuration fetched and parsed |

### Host postMessage Protocol
Custom UI iframes communicate with the parent \`<cakechat-widget>\` container using standard \`window.parent.postMessage\`:
- \`window.parent.postMessage({ type: 'cakechat:close' }, '*')\`: Collapse widget window.
- \`window.parent.postMessage({ type: 'cakechat:open' }, '*')\`: Expand widget window.
- \`window.parent.postMessage({ type: 'cakechat:toggle' }, '*')\`: Toggle open/closed state.
*(Alternatively, calling \`CakeChat.closeWidget()\`, \`CakeChat.openWidget()\`, or \`CakeChat.toggleWidget()\` dispatches these messages automatically).*

---

## 9. Production-Ready Starter Templates

### Vanilla JavaScript Starter (\`index.html\`)
\`\`\`html
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>CakeChat Custom UI</title>
  <script src="/api/WebBot/sdk"></script>
</head>
<body>
  <div id="app">
    <header>
      <span id="title">AI Assistant</span>
      <small id="userId"></small>
      <button id="clearBtn">Clear</button>
      <button onclick="CakeChat.closeWidget()">Close</button>
    </header>
    <div id="messages"></div>
    <footer>
      <input id="input" placeholder="Type a message..." />
      <button id="sendBtn">Send</button>
    </footer>
  </div>

  <script>
    const client = new CakeChat.CakeChatClient();
    document.getElementById('userId').textContent = 'User: ' + client.getUserId();
    document.getElementById('clearBtn').onclick = () => client.clearHistory();

    client.subscribe(state => {
      const msgs = document.getElementById('messages');
      msgs.innerHTML = state.messages.map(m => \`<div class="\${m.role}">\${m.message}</div>\`).join('');
      document.getElementById('sendBtn').disabled = state.isProcessing;
    });

    const send = () => {
      const inp = document.getElementById('input');
      if (inp.value.trim()) { client.sendMessage(inp.value); inp.value = ''; }
    };
    document.getElementById('sendBtn').onclick = send;
    document.getElementById('input').onkeydown = e => { if (e.key === 'Enter') send(); };

    client.connect();
  </script>
</body>
</html>
\`\`\`

### React Integration Starter (\`CustomChatApp.tsx\`)
\`\`\`tsx
import React from 'react';

// SDK provides factory function to create React Hooks:
const { CakeChatProvider, useCakeChat } = (window as any).CakeChat.createReactHooks(React);

export function CustomChatApp() {
  const {
    client,
    messages,
    input,
    setInput,
    sendMessage,
    isProcessing,
    isStreaming,
    connectionStatus,
    feedbackMessage,
    clearHistory
  } = useCakeChat();

  const userId = client?.getUserId();

  return (
    <div className="chat-container">
      <header className="header">
        <div>
          <span>Status: {connectionStatus}</span>
          <small style={{ marginLeft: 8, opacity: 0.7 }}>User: {userId}</small>
        </div>
        <div>
          <button onClick={() => clearHistory()}>Clear</button>
          <button onClick={() => (window as any).CakeChat.closeWidget()}>Close</button>
        </div>
      </header>

      <div className="message-list">
        {messages.map((msg, idx) => (
          <div key={idx} className={\`msg \${msg.role}\`}>
            <p>{msg.message}</p>
            {msg.mediaUrls?.map((m, mIdx) => (
              <img key={mIdx} src={m.url} alt="attachment" className="msg-media" />
            ))}
            {msg.role === 'assistant' && msg.historyId && (
              <div className="feedback">
                <button onClick={() => feedbackMessage(msg.historyId, 'Good')}>👍</button>
                <button onClick={() => feedbackMessage(msg.historyId, 'Bad')}>👎</button>
              </div>
            )}
          </div>
        ))}
        {isStreaming && <div className="typing">AI is typing...</div>}
      </div>

      <div className="input-bar">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Type a message..."
        />
        <button disabled={isProcessing || !input.trim()} onClick={() => sendMessage()}>
          Send
        </button>
      </div>
    </div>
  );
}
\`\`\`
`;
}

export const CUSTOM_UI_SDK_SPEC_MARKDOWN = buildCustomUiSdkSpecMarkdown();

export function CustomUiDevGuideModal({
  opened,
  onClose,
}: CustomUiDevGuideModalProps) {
  const [activeTab, setActiveTab] = useState<string | null>('cakechat');

  const { data: sdkDts, isLoading: isLoadingDts } = useQuery({
    queryKey: ['webbot-sdk-dts'],
    queryFn: async () => {
      const res = await fetch('/api/WebBot/sdk.d.ts');
      if (!res.ok) {
        throw new Error(`Failed to load sdk.d.ts: ${res.status}`);
      }
      return res.text();
    },
    enabled: opened,
    staleTime: 1000 * 60 * 10,
  });

  const specMarkdown = useMemo(() => buildCustomUiSdkSpecMarkdown(sdkDts), [sdkDts]);

  const handleDownloadMarkdown = () => {
    const blob = new Blob([specMarkdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cakechat-custom-ui-spec.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconBook size={20} color="var(--mantine-color-violet-6)" />
          <Text fw={600} size="md">
            自訂 UI (Custom UI) 開發指南與規格手冊
          </Text>
        </Group>
      }
      size="100%"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {/* Top Action & Alert Card */}
        <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-violet-light)">
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <div>
              <Group gap="xs">
                <IconSparkles size={18} color="var(--mantine-color-violet-7)" />
                <Text fw={600} size="sm" c="violet.9">
                  完整開發規格與技術手冊 (Markdown Reference)
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mt={2}>
                可直接下載或複製官方 Markdown 規格手冊，包含架構原理、User ID 整合、cakechat.json 規格、Headless SDK API 與型別宣告。
              </Text>
            </div>
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                leftSection={<IconDownload size={14} />}
                onClick={handleDownloadMarkdown}
              >
                下載規格 Markdown
              </Button>
              <CopyButton value={specMarkdown}>
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    color={copied ? 'teal' : 'violet'}
                    leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    onClick={copy}
                  >
                    {copied ? '已複製規格 Markdown' : '複製規格 Markdown'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Group>
        </Paper>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Custom UI 運行於沙盒 Iframe 中，平台已託管專屬 Headless SDK。只需在 HTML 中引入{' '}
          <Code>&lt;script src="/api/WebBot/sdk"&gt;&lt;/script&gt;</Code>
          ，即可使用全域物件 <Code>window.CakeChat</Code> 與客戶端 <Code>CakeChatClient</Code>。
        </Alert>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab} variant="outline" radius="sm">
          <Tabs.List mb="md">
            <Tabs.Tab value="cakechat" leftSection={<IconPackage size={15} />}>
              專案規範與 cakechat.json
            </Tabs.Tab>
            <Tabs.Tab value="user-auth" leftSection={<IconUser size={15} />}>
              訪客身分與會員整合 (User ID & Auth)
            </Tabs.Tab>
            <Tabs.Tab value="sdk" leftSection={<IconWorld size={15} />}>
              全域工具與 Client API
            </Tabs.Tab>
            <Tabs.Tab value="state-events" leftSection={<IconChartBar size={15} />}>
              狀態快照與事件監聽
            </Tabs.Tab>
            <Tabs.Tab value="extension" leftSection={<IconSparkles size={15} />}>
              擴充插件標籤 (&lt;cakechat-extension&gt;)
            </Tabs.Tab>
            <Tabs.Tab value="template" leftSection={<IconRocket size={15} />}>
              完整起手範例 (Starter Code)
            </Tabs.Tab>
          </Tabs.List>

          {/* Tab 1: cakechat.json & Package rules */}
          <Tabs.Panel value="cakechat">
            <Stack gap="md">
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  📦 ZIP 專案結構與上傳規範
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  打包為 <Code>.zip</Code> 檔案直接上傳，支援單一外層目錄自動去層（Strip Common Prefix）與全新部署（Clean Deploy）。
                </Text>
                <Code block style={{ fontSize: '11.5px', lineHeight: 1.5 }}>
{`my-custom-ui.zip
├── cakechat.json       (介面規格與推薦配置描述檔)
├── index.html          (預設入口 HTML 網頁)
├── style.css           (樣式表，需以相對路徑引用)
├── app.js              (前端邏輯代碼)
└── assets/             (圖檔、字型等靜態資源)`}
                </Code>
              </Paper>

              {/* cakechat.json field reference */}
              <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" mb="xs">
                  <div>
                    <Text fw={600} size="sm">
                      ⚙️ cakechat.json 設定檔規格
                    </Text>
                    <Text size="xs" c="dimmed">
                      設定檔置於 ZIP 根目錄，上傳時後台會自動讀取並同步推薦設定
                    </Text>
                  </div>
                  <CopyButton value={SAMPLE_CAKECHAT_JSON}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant="light"
                        color={copied ? 'teal' : 'violet'}
                        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        onClick={copy}
                      >
                        {copied ? '已複製 JSON' : '複製 cakechat.json 範本'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
                <Table withTableBorder withColumnBorders verticalSpacing="xs" mb="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 150 }}>欄位名稱</Table.Th>
                      <Table.Th style={{ width: 110 }}>型別</Table.Th>
                      <Table.Th style={{ width: 110 }}>預設值</Table.Th>
                      <Table.Th>說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>name</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">null</Text></Table.Td>
                      <Table.Td><Text size="xs">自訂 UI 主題名稱，例如 "Cyberpunk Dark Theme"</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>version</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">"1.0.0"</Text></Table.Td>
                      <Table.Td><Text size="xs">版本號碼</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>description</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">null</Text></Table.Td>
                      <Table.Td><Text size="xs">主題或 UI 功能簡介</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>author</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">null</Text></Table.Td>
                      <Table.Td><Text size="xs">開發者或團隊名稱</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>mode</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="blue">"window" | "headless"</Badge></Table.Td>
                      <Table.Td><Text size="xs">"window"</Text></Table.Td>
                      <Table.Td><Text size="xs">運作模式："window" 為懸浮視窗模式；"headless" 為無頭嵌入模式（相容 "full" 別名）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>entryFile</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">"index.html"</Text></Table.Td>
                      <Table.Td><Text size="xs">入口 HTML 檔案相對路徑</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>botConfig</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">object</Badge></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">null</Text></Table.Td>
                      <Table.Td><Text size="xs">推薦覆蓋的機器人外觀與設定物件（對應 SDK BotConfig 型別，如 layout.windowWidth, layout.windowHeight, theme, text, extensions 等）。上傳後後台會提供一鍵套用</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
                <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                  {SAMPLE_CAKECHAT_JSON}
                </Code>
              </Paper>

              {/* BotConfig TypeScript SDK schema */}
              <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" mb="xs">
                  <div>
                    <Text fw={600} size="sm">
                      🎨 Headless SDK TypeScript 型別宣告 (cakechat-sdk.d.ts)
                    </Text>
                    <Text size="xs" c="dimmed">
                      包含 <Code>BotConfig</Code>、<Code>CakeChatClient</Code> 及全部型別定義（動態源自 <Code>/api/WebBot/sdk.d.ts</Code>）
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      component="a"
                      href="/api/WebBot/sdk.d.ts"
                      download="cakechat-sdk.d.ts"
                      leftSection={<IconDownload size={14} />}
                    >
                      下載 .d.ts
                    </Button>
                    <CopyButton value={sdkDts || ''}>
                      {({ copied, copy }) => (
                        <Button
                          size="xs"
                          variant="light"
                          color={copied ? 'teal' : 'violet'}
                          leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                          onClick={copy}
                          disabled={!sdkDts}
                        >
                          {copied ? '已複製型別定義' : '複製完整型別定義 (.d.ts)'}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>
                </Group>
                <ScrollArea.Autosize mah={320}>
                  {isLoadingDts ? (
                    <Text size="xs" c="dimmed" p="xs">
                      載入 SDK 型別定義中...
                    </Text>
                  ) : (
                    <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                      {sdkDts || '// 暫時無法載入 sdk.d.ts，請直接點選「下載 .d.ts」或至 /api/WebBot/sdk.d.ts 檢視'}
                    </Code>
                  )}
                </ScrollArea.Autosize>
              </Paper>

              {/* URL Params table */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  🔗 容器自動注入 URL 查詢參數表
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  外層 ChatWidget 載入 Iframe 時會在網址附帶以下參數，SDK 會自動解析：
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 150 }}>參數名稱</Table.Th>
                      <Table.Th>說明與用途</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>botProviderId</Code></Table.Td>
                      <Table.Td><Text size="xs">當前 WebBot 機器人的唯一 ID，SDK 連線必備參數</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>host</Code></Table.Td>
                      <Table.Td><Text size="xs">API 與 SignalR 服務伺服器主機網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>userId</Code></Table.Td>
                      <Table.Td><Text size="xs">目前訪客/會員識別碼（由宿主容器或網址傳入，外層自動轉發）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>userToken</Code></Table.Td>
                      <Table.Td><Text size="xs">訪客驗證用 JWT Token（若開啟訪客身分驗證時提供）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>previewMode</Code></Table.Td>
                      <Table.Td><Text size="xs">若為 "true" 表示處於後台管理員即時預覽模式</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>mode</Code></Table.Td>
                      <Table.Td><Text size="xs">目前運作模式："window"（懸浮窗）或 "headless"（滿版嵌入，相容 "full"）</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          </Tabs.Panel>

          {/* Tab 2: User ID & Member Auth Integration (NEW) */}
          <Tabs.Panel value="user-auth">
            <Stack gap="md">
              <Alert icon={<IconUser size={16} />} color="teal" variant="light">
                <Text fw={600} size="sm" mb={4}>
                  訪客識別碼 (User ID) 是 CakeChat 對話隔離與使用者資料的核心分組鍵
                </Text>
                <Text size="xs">
                  所有對話歷史紀錄（<Code>/api/WebBot/history/&#123;botProviderId&#125;/&#123;userId&#125;</Code>）、即時 SignalR 廣播通道（<Code>ListenEvent</Code>）、評價反饋及清空歷史操作，皆嚴格依據 <Code>(botProviderId, userId)</Code> 進行隔離。
                </Text>
              </Alert>

              {/* Resolution Priority */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  🎯 User ID 4 級解析優先順序 (Resolution Priority)
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  初始化 <Code>new CakeChat.CakeChatClient()</Code> 時，SDK 會依以下階層自動解析使用者身分：
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 100 }}>優先序</Table.Th>
                      <Table.Th style={{ width: 220 }}>解析來源</Table.Th>
                      <Table.Th>運作說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="red">Priority 1</Badge></Table.Td>
                      <Table.Td><Code fw={600}>options.userId</Code></Table.Td>
                      <Table.Td><Text size="xs">建構實例時顯式傳入的字串，例如 <Code>new CakeChatClient(&#123; userId: 'member_9527' &#125;)</Code>。最高優先級。</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="orange">Priority 2</Badge></Table.Td>
                      <Table.Td><Code fw={600}>URL Query (?userId=...)</Code></Table.Td>
                      <Table.Td><Text size="xs">自網址查詢參數讀取。宿主容器載入沙盒 Iframe 時會自動轉發此參數。</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="yellow">Priority 3</Badge></Table.Td>
                      <Table.Td><Code fw={600}>本地快取 (Storage Cache)</Code></Table.Td>
                      <Table.Td><Text size="xs">從指定 Storage 尋找鍵值 <Code>chatbot_userid_&#123;botProviderId&#125;</Code> 中先前持久化保存的訪客 ID。</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="green">Priority 4</Badge></Table.Td>
                      <Table.Td><Code fw={600}>自動生成匿名 UUID</Code></Table.Td>
                      <Table.Td><Text size="xs">若上述皆無，SDK 會自動生成 <Code>user_&#123;crypto.randomUUID()&#125;</Code>，並自動存入 Storage 快取。</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              {/* Member System Integration Guide */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  🏢 外部會員與帳號系統整合指南
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  若網站有專屬會員系統，可將已登入的會員 ID 傳入，使對話紀錄與該會員帳號完全綁定：
                </Text>

                <Stack gap="xs">
                  <Text size="xs" fw={600}>
                    方式 A：透過外層 Web Component 標籤傳遞（推薦）
                  </Text>
                  <Text size="xs" c="dimmed">
                    在宿主頁面的 <Code>&lt;cakechat-widget&gt;</Code> 標籤直接填入會員屬性，宿主容器會自動將參數傳給 Custom UI：
                  </Text>
                  <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
{`<!-- 在宿主網站 HTML 中引入 -->
<cakechat-widget
  bot-provider-id="65d4fa3901b2a94f1c998a12"
  user-id="member_9527"
  user-token="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...">
</cakechat-widget>`}
                  </Code>

                  <Text size="xs" fw={600} mt="xs">
                    方式 B：在 Custom UI 前端代碼中顯式實例化
                  </Text>
                  <Text size="xs" c="dimmed">
                    若您的 Custom UI 擁有獨立的使用者輸入或登入介面，可在建構時直接帶入：
                  </Text>
                  <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
{`// 顯式傳入特定會員 ID 與驗證 Token
const client = new CakeChat.CakeChatClient({
  userId: 'member_9527',
  userToken: userJwtToken,
  storage: 'local' // 保存在 localStorage
});

// 啟動連線（將自動以 member_9527 建立 SignalR 連線並載入其個人歷史對話）
await client.connect();`}
                  </Code>
                </Stack>
              </Paper>

              {/* Storage Strategy Table */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  💾 本地快取適配器策略 (Storage Strategy)
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  可透過 <Code>options.storage</Code> 控制匿名訪客 ID 的快取策略與跨分頁生命週期：
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 140 }}>儲存模式</Table.Th>
                      <Table.Th style={{ width: 130 }}>生命週期</Table.Th>
                      <Table.Th>運作說明與建議情境</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="blue">'local' (預設)</Badge></Table.Td>
                      <Table.Td><Text size="xs">持久保存</Text></Table.Td>
                      <Table.Td><Text size="xs">儲存於 <Code>localStorage</Code>。跨分頁共用同一個訪客身分，重啟瀏覽器後仍保留先前對話紀錄。</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="cyan">'session'</Badge></Table.Td>
                      <Table.Td><Text size="xs">當前分頁</Text></Table.Td>
                      <Table.Td><Text size="xs">儲存於 <Code>sessionStorage</Code>。關閉分頁或新開分頁時會產生獨立訪客 ID，適合一次性或無留存需求的訪客諮詢。</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="gray">'memory'</Badge></Table.Td>
                      <Table.Td><Text size="xs">記憶體易失</Text></Table.Td>
                      <Table.Td><Text size="xs">儲存於 JS 記憶體變數中。頁面重新整理（F5）即重設身分與對話。</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Badge size="xs" color="violet">StorageAdapter</Badge></Table.Td>
                      <Table.Td><Text size="xs">自訂實作</Text></Table.Td>
                      <Table.Td><Text size="xs">傳入包含 <Code>&#123; getItem, setItem, removeItem &#125;</Code> 的自訂物件，可整合 Cookie 或 IndexedDB 等機制。</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              {/* Read User ID API */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  🔍 取得當前訪客識別碼
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  在介面上顯示「訪客 ID」或發送分析事件時，可直接調用 API 取得目前解析出的身分：
                </Text>
                <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
{`// 取得解析後的當前訪客/會員 ID
const userId = client.getUserId();
console.log('當前訪客 ID:', userId);

// 範例：在畫面上顯示訪客標籤
document.getElementById('userBadge').textContent = 'ID: ' + userId;`}
                </Code>
              </Paper>
            </Stack>
          </Tabs.Panel>

          {/* Tab 3: SDK API & Controls */}
          <Tabs.Panel value="sdk">
            <Stack gap="md">
              {/* Global Window Controls */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  🌐 全域視窗控制與工具 (window.CakeChat)
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  與宿主外層容器溝通（如關閉視窗、展開視窗）及解析網址設定
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 250 }}>方法名稱</Table.Th>
                      <Table.Th style={{ width: 140 }}>回傳值</Table.Th>
                      <Table.Th>說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.closeWidget()</Code></Table.Td>
                      <Table.Td><Code>void</Code></Table.Td>
                      <Table.Td><Text size="xs">通知外層容器關閉/收合懸浮聊天視窗（頂部關閉按鈕首選）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.openWidget()</Code></Table.Td>
                      <Table.Td><Code>void</Code></Table.Td>
                      <Table.Td><Text size="xs">通知外層容器展開/開啟懸浮聊天視窗</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.toggleWidget()</Code></Table.Td>
                      <Table.Td><Code>void</Code></Table.Td>
                      <Table.Td><Text size="xs">切換外層懸浮聊天視窗展開/關閉狀態</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.getSearchParams()</Code></Table.Td>
                      <Table.Td><Code>Record&lt;string, string&gt;</Code></Table.Td>
                      <Table.Td><Text size="xs">取得 URL 傳入的所有 Query 參數鍵值物件</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.fetchBotConfig(options)</Code></Table.Td>
                      <Table.Td><Code>Promise&lt;BotConfig&gt;</Code></Table.Td>
                      <Table.Td><Text size="xs">向遠端 API 取得後台設定的機器人外觀配置 (含標題、顏色、頭像等)</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.getBotAvatarUrl(options?)</Code></Table.Td>
                      <Table.Td><Code>string | null</Code></Table.Td>
                      <Table.Td><Text size="xs">解析機器人頭像圖片完整網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.getBotWidgetIconUrl(options?)</Code></Table.Td>
                      <Table.Td><Code>string | null</Code></Table.Td>
                      <Table.Td><Text size="xs">解析懸浮啟動圖示 (Widget Launcher) 完整網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.getBotBackgroundImageUrl(options?)</Code></Table.Td>
                      <Table.Td><Code>string | null</Code></Table.Td>
                      <Table.Td><Text size="xs">解析聊天室背景圖片完整網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>CakeChat.createReactHooks(React)</Code></Table.Td>
                      <Table.Td><Code>ReactHooksBundle</Code></Table.Td>
                      <Table.Td><Text size="xs">若使用 React，可生成 CakeChatProvider 與 useCakeChat 等 Hooks</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              {/* CakeChatClient Options */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  ⚙️ CakeChatClient 建構選項 (CakeChatClientOptions)
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 180 }}>參數名稱</Table.Th>
                      <Table.Th style={{ width: 140 }}>型別</Table.Th>
                      <Table.Th style={{ width: 100 }}>預設值</Table.Th>
                      <Table.Th>說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>botProviderId</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">URL 參數</Text></Table.Td>
                      <Table.Td><Text size="xs">機器人 Provider ID，未傳時自動自 URL Query 解析</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>host</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">Origin</Text></Table.Td>
                      <Table.Td><Text size="xs">後端主機 API 網址，未傳時自動自 URL Query 或當前 Origin 解析</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>userId</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">自動解析</Text></Table.Td>
                      <Table.Td><Text size="xs">訪客/會員 ID，未傳時依 4 級優先序解析或生成隨機 UUID</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>userToken</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">undefined</Text></Table.Td>
                      <Table.Td><Text size="xs">訪客驗證用 JWT Token（若開啟驗證時必填）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>storage</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="blue">'local' | 'session' | 'memory' | Adapter</Badge></Table.Td>
                      <Table.Td><Text size="xs">'local'</Text></Table.Td>
                      <Table.Td><Text size="xs">本地儲存策略，預設為持久保存的 localStorage</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>storageKeyPrefix</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">'chatbot_userid_'</Text></Table.Td>
                      <Table.Td><Text size="xs">Storage 中保存訪客 ID 的鍵名前綴</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>sleepTimeout</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">number</Badge></Table.Td>
                      <Table.Td><Text size="xs">60</Text></Table.Td>
                      <Table.Td><Text size="xs">無操作閒置休眠秒數，設為 0 停用休眠機制</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>autoConnect</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">boolean</Badge></Table.Td>
                      <Table.Td><Text size="xs">false</Text></Table.Td>
                      <Table.Td><Text size="xs">實例化時是否自動發起連線</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>previewMode</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">boolean</Badge></Table.Td>
                      <Table.Td><Text size="xs">false</Text></Table.Td>
                      <Table.Td><Text size="xs">是否處於後台管理員預覽模式（預覽模式下使用模擬對話，不送出遠端請求）</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              {/* CakeChatClient Methods */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  ⚡ CakeChatClient 客戶端完整 API (18+ 方法)
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  包含連線控制、訊息發送、歷史管理、滿意度評價、身分與屬性查詢
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 260 }}>方法名稱與簽章</Table.Th>
                      <Table.Th style={{ width: 80 }}>類型</Table.Th>
                      <Table.Th>功能說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>new CakeChatClient(options?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="violet">Constructor</Badge></Table.Td>
                      <Table.Td><Text size="xs">建立實例。不傳參數時自動從網址 Query 進行零配置初始化（Zero-Config）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.connect()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">建立 SignalR 即時雙向連線並自動拉取歷史對話紀錄</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.disconnect()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">中斷目前即時通訊連線</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.retryConnection()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">手動重新連線 SignalR 服務</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.sendMessage(text?, retryId?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">送出使用者訊息。若未帶參則發送當前 input 快照文字並自動清空輸入框</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.retryMessage(historyId)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">重新發送特定發送失敗的使用者訊息</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.loadHistory(silent?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">重新自遠端伺服器拉取目前使用者的最新歷史對話</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.clearHistory()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">清空本地記憶體與雲端伺服器該使用者的歷史對話紀錄</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.feedbackMessage(id, rating, comment?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">針對特定 AI 回覆提交滿意度評分（'Good' | 'Bad'）與可選意見回饋</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.reportIssue(id, comment)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">針對該則回覆回報問題至系統後台</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.setInput(text)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">同步設定當前輸入文字快照並重設閒置休眠計時器</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.wake()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">喚醒休眠中的機器人（轉為 'idle' 狀態）並重設計時器</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getUserId()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得當前解析成功的訪客或會員識別碼（User ID）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getBotState()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得機器人狀態機狀態：'idle' | 'sleep' | 'thinking' | 'streaming' | 'error'</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getState()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得當前響應式狀態快照物件（CakeChatState）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.subscribe(listener)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">訂閱狀態變化 <Code>(state, prevState) =&gt; void</Code>，回傳取消訂閱函式</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.on(event, handler)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">監聽指定事件（如 'messageReceived', 'stateChange'），回傳取消監聽函式</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.off(event, handler)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">移除指定事件監聽器</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getConfig()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得已快取的機器人配置物件（BotConfig）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.isConfigLoaded()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">檢查機器人配置是否已成功載入</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.fetchConfig(forceReload?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="indigo">Async</Badge></Table.Td>
                      <Table.Td><Text size="xs">向伺服器非同步拉取最新機器人配置物件</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getAvatarUrl(options?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得機器人頭像圖片完整網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getWidgetIconUrl(options?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得懸浮按鈕圖示完整網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.getBackgroundImageUrl(options?)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">取得聊天視窗背景底圖完整網址</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.setFeedbackNotificationText(text)</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="teal">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">動態自訂訪客點選評價後顯示的推播提示文字</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>client.destroy()</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="red">Method</Badge></Table.Td>
                      <Table.Td><Text size="xs">銷毀客戶端實例，清除休眠計時器、推播計時器與全部事件監聽器</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          </Tabs.Panel>

          {/* Tab 4: State & Events */}
          <Tabs.Panel value="state-events">
            <Stack gap="md">
              {/* CakeChatState Structure */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  📊 CakeChatState 狀態快照欄位
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 170 }}>欄位名稱</Table.Th>
                      <Table.Th style={{ width: 220 }}>型別</Table.Th>
                      <Table.Th>說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>messages</Code></Table.Td>
                      <Table.Td><Code>ChatRecord[]</Code></Table.Td>
                      <Table.Td><Text size="xs">對話訊息陣列，包含 role ('user' | 'assistant')、message、historyId、feedback 與 mediaUrls</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>input</Code></Table.Td>
                      <Table.Td><Code>string</Code></Table.Td>
                      <Table.Td><Text size="xs">當前輸入框文字快照</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>isProcessing</Code></Table.Td>
                      <Table.Td><Code>boolean</Code></Table.Td>
                      <Table.Td><Text size="xs">AI 正在思考或產生回覆內容（可用於停用發送按鈕）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>isStreaming</Code></Table.Td>
                      <Table.Td><Code>boolean</Code></Table.Td>
                      <Table.Td><Text size="xs">AI 正在即時串流打字輸出中</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>isLoadingHistory</Code></Table.Td>
                      <Table.Td><Code>boolean</Code></Table.Td>
                      <Table.Td><Text size="xs">是否正在向伺服器載入歷史對話紀錄</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>botState</Code></Table.Td>
                      <Table.Td><Code>'idle' | 'sleep' | 'thinking' | 'streaming' | 'error'</Code></Table.Td>
                      <Table.Td><Text size="xs">機器人狀態機狀態</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>connectionStatus</Code></Table.Td>
                      <Table.Td><Code>'disconnected' | 'connecting' | 'connected' | 'reconnecting'</Code></Table.Td>
                      <Table.Td><Text size="xs">SignalR 即時連線狀態</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>error</Code></Table.Td>
                      <Table.Td><Code>string | null</Code></Table.Td>
                      <Table.Td><Text size="xs">最新錯誤訊息字串或 null</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>notification</Code></Table.Td>
                      <Table.Td><Code>string | null</Code></Table.Td>
                      <Table.Td><Text size="xs">伺服器系統推播通知訊息</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              {/* ChatRecord data structure */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  💬 ChatRecord 訊息資料結構
                </Text>
                <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
{`interface ChatRecord {
  role: 'user' | 'assistant' | string;
  message: string;             // Markdown 格式訊息文字
  historyId?: string;          // 唯一訊息識別碼 (用於評分回饋與重試)
  status?: 'sending' | 'sent' | 'failed'; // 使用者訊息發送狀態
  feedback?: {
    rating?: 'Good' | 'Bad';
    comment?: string;
    timestamp?: number;
  };
  timestamp?: number;          // 訊息時間戳記
  mediaUrls?: Array<{          // 多媒體附圖或檔案清單
    url: string;
    type: string;
  }>;
}`}
                </Code>
              </Paper>

              {/* Event List */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  📡 事件監聽清單 (client.on)
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 220 }}>事件名稱 (event)</Table.Th>
                      <Table.Th style={{ width: 250 }}>Payload 資料內容</Table.Th>
                      <Table.Th>觸發時機與說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>stateChange</Code></Table.Td>
                      <Table.Td><Code>{`{ state, previousState, isThinking, isStreaming }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">機器人狀態轉移（例如進入休眠或開始思考）時觸發</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>messageReceived</Code></Table.Td>
                      <Table.Td><Code>{`{ role, message, text, isComplete, historyId? }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">接收到 AI 回覆文字或串流 Chunk 更新時觸發</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>messageSent</Code></Table.Td>
                      <Table.Td><Code>{`{ text, message, timestamp, historyId? }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">使用者訊息成功送出時觸發</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>connectionStatusChange</Code></Table.Td>
                      <Table.Td><Code>{`{ status, previousStatus, error? }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">即時連線狀態切換時觸發</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>error</Code></Table.Td>
                      <Table.Td><Code>{`{ error: string }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">通訊或處理發生異常錯誤時觸發</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>notification</Code></Table.Td>
                      <Table.Td><Code>{`{ message: string }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">收到伺服器推播通知時觸發</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>configLoaded</Code></Table.Td>
                      <Table.Td><Code>{`{ config: BotConfig }`}</Code></Table.Td>
                      <Table.Td><Text size="xs">機器人後台配置載入完成時觸發</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              {/* postMessage Communication */}
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  🪟 沙盒 Iframe 宿主視窗通訊協定 (postMessage)
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                  Custom UI 運行於 Iframe 內，與外層宿主 ChatWidget 透過標準 <Code>window.parent.postMessage</Code> 協同運作：
                </Text>
                <Table withTableBorder withColumnBorders verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 220 }}>postMessage 格式</Table.Th>
                      <Table.Th style={{ width: 180 }}>SDK 簡化封裝</Table.Th>
                      <Table.Th>效果說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code>{`{ type: 'cakechat:close' }`}</Code></Table.Td>
                      <Table.Td><Code>CakeChat.closeWidget()</Code></Table.Td>
                      <Table.Td><Text size="xs">通知宿主收合/關閉聊天懸浮視窗</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code>{`{ type: 'cakechat:open' }`}</Code></Table.Td>
                      <Table.Td><Code>CakeChat.openWidget()</Code></Table.Td>
                      <Table.Td><Text size="xs">通知宿主展開/開啟聊天懸浮視窗</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code>{`{ type: 'cakechat:toggle' }`}</Code></Table.Td>
                      <Table.Td><Code>CakeChat.toggleWidget()</Code></Table.Td>
                      <Table.Td><Text size="xs">切換展開/收合狀態</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          </Tabs.Panel>

          {/* Tab: Declarative <cakechat-extension> */}
          <Tabs.Panel value="extension">
            <Stack gap="md">
              <Paper withBorder p="sm" radius="md">
                <Text fw={600} size="sm" mb="xs">
                  ✨ 宣告式擴充插件標籤 (&lt;cakechat-extension&gt;)
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                  在 Custom UI 或自訂網站排版中，引入 SDK 後即可直接使用宣告式 Web Component 或 React 元件，輕鬆呈現機器人專屬的看板娘、Spine 2D 骨骼動畫、圖片狀態輪播或自訂 HTML 插件，並自動與聊天狀態（思考、說話串流、待機休眠）即時連動。
                </Text>

                <Tabs defaultValue="html" variant="pills" radius="xs" mb="md">
                  <Tabs.List mb="xs">
                    <Tabs.Tab value="html" size="xs">Vanilla HTML / Vue</Tabs.Tab>
                    <Tabs.Tab value="react" size="xs">React (&lt;CakeChatExtension /&gt;)</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="html">
                    <Paper withBorder p="xs" bg="dark.8" radius="sm" style={{ position: 'relative' }}>
                      <CopyButton value={`<!-- 預設載入第 1 個啟用的擴充插件 (index=0) -->\n<cakechat-extension></cakechat-extension>\n\n<!-- 或指定特定角色資源包與尺寸 -->\n<div style="width: 280px; height: 360px;">\n  <cakechat-extension bundle="my-spine-character"></cakechat-extension>\n</div>\n\n<!-- 手動鎖定特定動作狀態 -->\n<cakechat-extension state="speaking" state-locked></cakechat-extension>`}>
                        {({ copied, copy }) => (
                          <Button
                            size="xs"
                            variant="subtle"
                            color={copied ? 'teal' : 'gray'}
                            onClick={copy}
                            style={{ position: 'absolute', top: 6, right: 6 }}
                            leftSection={copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
                          >
                            {copied ? '已複製' : '複製代碼'}
                          </Button>
                        )}
                      </CopyButton>
                      <pre style={{ margin: 0, fontSize: 12, color: '#e2e8f0', overflowX: 'auto' }}>
                        {`<!-- 預設載入第 1 個啟用的擴充插件 (index=0) -->
<cakechat-extension></cakechat-extension>

<!-- 或指定特定角色資源包與尺寸 -->
<div style="width: 280px; height: 360px;">
  <cakechat-extension bundle="my-spine-character"></cakechat-extension>
</div>

<!-- 手動鎖定特定動作狀態 -->
<cakechat-extension state="speaking" state-locked></cakechat-extension>`}
                      </pre>
                    </Paper>
                  </Tabs.Panel>

                  <Tabs.Panel value="react">
                    <Paper withBorder p="xs" bg="dark.8" radius="sm" style={{ position: 'relative' }}>
                      <CopyButton value={`import React from 'react';\n\n// 透過 createReactHooks 綁定專案的 React 實例\nconst { CakeChatProvider, CakeChatExtension, useCakeChat } = window.CakeChat.createReactHooks(React);\n\nfunction MyCustomChat() {\n  return (\n    <div className="custom-chat-layout">\n      {/* 自由排版擺放 Mascot 角色 */}\n      <CakeChatExtension\n        bundle="my-spine-character"\n        onClick={(e) => console.log('點擊角色', e.detail)}\n      />\n      <div className="messages-box">...</div>\n    </div>\n  );\n}`}>
                        {({ copied, copy }) => (
                          <Button
                            size="xs"
                            variant="subtle"
                            color={copied ? 'teal' : 'gray'}
                            onClick={copy}
                            style={{ position: 'absolute', top: 6, right: 6 }}
                            leftSection={copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
                          >
                            {copied ? '已複製' : '複製代碼'}
                          </Button>
                        )}
                      </CopyButton>
                      <pre style={{ margin: 0, fontSize: 12, color: '#e2e8f0', overflowX: 'auto' }}>
                        {`import React from 'react';

// 透過 createReactHooks 綁定專案的 React 實例
const { CakeChatProvider, CakeChatExtension, useCakeChat } = window.CakeChat.createReactHooks(React);

function MyCustomChat() {
  return (
    <div className="custom-chat-layout">
      {/* 自由排版擺放 Mascot 角色 */}
      <CakeChatExtension
        bundle="my-spine-character"
        onClick={(e) => console.log('點擊角色', e.detail)}
      />
      <div className="messages-box">...</div>
    </div>
  );
}`}
                      </pre>
                    </Paper>
                  </Tabs.Panel>
                </Tabs>

                <Text fw={600} size="xs" mb="xs">
                  標籤屬性 (Attributes / Props)
                </Text>
                <Table striped highlightOnHover withTableBorder withColumnBorders fz="xs" mb="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>屬性名稱</Table.Th>
                      <Table.Th>型別</Table.Th>
                      <Table.Th>預設值</Table.Th>
                      <Table.Th>說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>index</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">number</Badge></Table.Td>
                      <Table.Td><Text size="xs">0</Text></Table.Td>
                      <Table.Td><Text size="xs">指向機器人設定中已啟用插件的零基索引（預設為第 1 個）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>bundle</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">-</Text></Table.Td>
                      <Table.Td><Text size="xs">直接指定特定 Asset Bundle 目錄名稱（跳過 index 尋找）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>slot-id</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">-</Text></Table.Td>
                      <Table.Td><Text size="xs">依機器人後台設定的擴充插槽 ID 鎖定目標插件（React prop: slotId）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>state</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">string</Badge></Table.Td>
                      <Table.Td><Text size="xs">自動</Text></Table.Td>
                      <Table.Td><Text size="xs">當前動畫狀態（idle / thinking / speaking / sleep / error 等）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>state-locked</Code></Table.Td>
                      <Table.Td><Badge size="xs" color="gray">boolean</Badge></Table.Td>
                      <Table.Td><Text size="xs">false</Text></Table.Td>
                      <Table.Td><Text size="xs">鎖定狀態，暫停與聊天客戶端的自動連動，維持手動指定狀態</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>

                <Text fw={600} size="xs" mb="xs">
                  DOM 操作方法 (Methods on &lt;cakechat-extension&gt;)
                </Text>
                <Table striped highlightOnHover withTableBorder withColumnBorders fz="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>方法名稱</Table.Th>
                      <Table.Th>參數與簽名</Table.Th>
                      <Table.Th>說明</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>el.setState(state, lock?)</Code></Table.Td>
                      <Table.Td><Code>(state: string, lock?: boolean) =&gt; void</Code></Table.Td>
                      <Table.Td><Text size="xs">設定當前狀態，可選鎖定避免被聊天事件覆蓋</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>el.playAnimation(name, options?)</Code></Table.Td>
                      <Table.Td><Code>(name: string, options?: &#123; loop?: boolean &#125;) =&gt; void</Code></Table.Td>
                      <Table.Td><Text size="xs">立即播放特定動畫動作（如 wave、dance、nod 等）</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>el.resetState()</Code></Table.Td>
                      <Table.Td><Code>() =&gt; void</Code></Table.Td>
                      <Table.Td><Text size="xs">解除手動鎖定，恢復自動跟隨聊天狀態機</Text></Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td><Code fw={600}>el.reload()</Code></Table.Td>
                      <Table.Td><Code>() =&gt; Promise&lt;void&gt;</Code></Table.Td>
                      <Table.Td><Text size="xs">重新拉取 extension.json 並重新初始化渲染</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          </Tabs.Panel>

          {/* Tab 5: Starter Code Template */}
          <Tabs.Panel value="template">
            <Stack gap="md">
              {/* index.html Starter */}
              <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" mb="xs">
                  <div>
                    <Text fw={600} size="sm">
                      🚀 完整精簡起手範例：index.html (Vanilla JS)
                    </Text>
                    <Text size="xs" c="dimmed">
                      包含訪客 ID 顯示、清空對話、訊息串流、多媒體附圖、滿意度評分與視窗收合功能，開箱即用
                    </Text>
                  </div>
                  <CopyButton value={SAMPLE_INDEX_HTML}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant="light"
                        color={copied ? 'teal' : 'violet'}
                        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        onClick={copy}
                      >
                        {copied ? '已複製 index.html' : '複製 index.html 範本'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
                <ScrollArea.Autosize mah={300}>
                  <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                    {SAMPLE_INDEX_HTML}
                  </Code>
                </ScrollArea.Autosize>
              </Paper>

              {/* React Example */}
              <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" mb="xs">
                  <div>
                    <Text fw={600} size="sm">
                      ⚛️ React 專案整合起手範例
                    </Text>
                    <Text size="xs" c="dimmed">
                      搭配 <Code>CakeChat.createReactHooks(React)</Code> 快速整合 React 元件，展示 User ID 與清空對話
                    </Text>
                  </div>
                  <CopyButton value={SAMPLE_REACT_EXAMPLE}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant="light"
                        color={copied ? 'teal' : 'violet'}
                        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        onClick={copy}
                      >
                        {copied ? '已複製 React 範本' : '複製 React 範本'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
                <ScrollArea.Autosize mah={220}>
                  <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                    {SAMPLE_REACT_EXAMPLE}
                  </Code>
                </ScrollArea.Autosize>
              </Paper>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Divider my="md" />

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          關閉手冊
        </Button>
      </Group>
    </Modal>
  );
}
